import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["form", "input", "submit", "conversation", "welcome", "status"];

  connect() {
    this.history = [];
    this.enabled = !this.inputTarget.disabled;
  }

  disconnect() { this.clear(); }

  example(event) {
    this.inputTarget.value = event.currentTarget.textContent.trim();
    this.inputTarget.focus();
  }

  async send(event) {
    event.preventDefault();
    const content = this.inputTarget.value.trim();
    if (!content || this.pending) return;

    const messages = [...this.history.slice(-8), { role: "user", content }];
    // Remove whole pairs, retaining alternating roles, to fit the server's text budget.
    while (new TextEncoder().encode(JSON.stringify(messages)).length > 59000 && messages.length > 1) messages.splice(0, 2);
    const request = new AbortController();
    this.pending = request;
    this.submitTarget.disabled = true;
    this.inputTarget.disabled = true;
    this.welcomeTarget.hidden = true;
    this.appendMessage("user", content);
    this.statusTarget.textContent = "正在理解问题并查询授权项目…";

    try {
      const response = await fetch(this.formTarget.action, {
        method: "POST", credentials: "same-origin", cache: "no-store", signal: request.signal,
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ messages }),
      });
      if (response.redirected) throw new Error("登录已过期，请刷新页面后重新登录。");
      if (!response.headers.get("Content-Type")?.includes("application/json")) throw new Error("查询暂时无法完成，请稍后重试。");
      const data = await response.json();
      if (this.pending !== request) return;
      if (!response.ok) throw new Error(data.error?.message || "查询失败，请稍后重试。");
      this.history = [...messages, { role: "assistant", content: data.text }].slice(-8);
      this.appendMessage("assistant", data.text, data.projects, data.tools);
      this.inputTarget.value = "";
      this.statusTarget.textContent = "查询完成，结果以当前系统记录为准。";
    } catch (error) {
      if (this.pending !== request || error.name === "AbortError") return;
      this.appendMessage("assistant", error.message);
      this.statusTarget.textContent = "本次查询未完成，可调整问题后重新发送。";
    } finally {
      if (this.pending === request) {
        this.pending = null;
        this.submitTarget.disabled = false;
        this.inputTarget.disabled = false;
        this.inputTarget.focus();
      }
    }
  }

  appendMessage(role, text, projects = [], tools = []) {
    const container = document.createElement("article");
    container.dataset.assistantMessage = role;
    container.className = role === "user" ? "ml-auto max-w-full rounded-xl bg-primary/10 p-4 sm:max-w-[85%]" : "min-w-0 rounded-xl bg-base-200/60 p-4";
    const label = document.createElement("p");
    label.className = "mb-2 text-xs font-medium text-base-content/60";
    label.textContent = role === "user" ? "你" : "业务助手";
    const body = document.createElement("p");
    body.className = "whitespace-pre-wrap break-words text-sm leading-7 [overflow-wrap:anywhere]";
    body.textContent = text;
    container.append(label, body);

    if (tools.length) {
      const source = document.createElement("p");
      source.className = "mt-3 text-xs text-base-content/50";
      const names = { projects_list: "项目列表", projects_get_progress: "项目进度", projects_summary: "项目概览" };
      source.textContent = `已查询：${[...new Set(tools.map((tool) => names[tool.name]).filter(Boolean))].join("、")}`;
      container.append(source);
    }
    if (projects.length) {
      const links = document.createElement("div");
      links.className = "mt-3 flex flex-wrap gap-2";
      for (const project of projects) {
        if (!/^\/projects\/[a-f0-9-]{36}$/.test(project.url)) continue;
        const link = document.createElement("a");
        link.className = "btn btn-outline btn-xs h-auto max-w-full whitespace-normal break-words py-2";
        link.href = project.url;
        link.textContent = project.name;
        links.append(link);
      }
      container.append(links);
    }
    this.conversationTarget.append(container);
    this.conversationTarget.scrollTop = this.conversationTarget.scrollHeight;
  }

  clear() {
    this.pending?.abort();
    this.pending = null;
    this.history = [];
    this.element.querySelectorAll("[data-assistant-message]").forEach((message) => message.remove());
    if (this.hasInputTarget) this.inputTarget.value = "";
    if (this.hasWelcomeTarget) this.welcomeTarget.hidden = false;
    if (this.hasStatusTarget) this.statusTarget.textContent = "查询结果以当前系统记录为准。";
    if (this.hasInputTarget && this.hasSubmitTarget) {
      this.inputTarget.disabled = !this.enabled;
      this.submitTarget.disabled = !this.enabled;
    }
  }
}
